import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { Feature, FeatureCollection, Point } from 'geojson'
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { XyzTileModel } from '../models/xyz-tile.model'
import {
  NonDetectionProperties,
  NonDetectionResponseProperties,
} from '../models/non-detection.model'

@Injectable()
export class DataNonDetectionsService {
  private nonDetections: FeatureCollection<Point, NonDetectionProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  private cancelPreviousRequest$ = new Subject<void>()
  public isDataInitialized = false

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadNonDetectionsData(
    isHistoryEnabled: boolean,
    xyzTiles: XyzTileModel[],
    timestamp?: Date,
    bounds?: maplibregl.LngLatBounds,
  ): Promise<this> {
    this.cancelCurrentRequests()
    this.isDataInitialized = true

    if (isHistoryEnabled) {
      if (!timestamp || !bounds) {
        this.nonDetections.features = []
        return this
      }

      const historicFeatures = await this.loadHistoricNonDetections(timestamp, bounds)
      this.nonDetections.features = this.aggregateNonDetections([historicFeatures])
      return this
    }

    if (xyzTiles.length === 0) {
      this.nonDetections.features = []
      return this
    }

    const tileRequests = xyzTiles.map((tile) => this.loadNonDetections(tile))
    const results = await firstValueFrom(combineLatest(tileRequests))
    this.nonDetections.features = this.aggregateNonDetections(results)

    return this
  }

  async loadHistoricNonDetections(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<Point, NonDetectionResponseProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({ accept: 'json' }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }
    const url = new URL(`${this.config.backend_url}vzo/nondetections/historic`)
    return firstValueFrom(
      this.http
        .get<FeatureCollection<Point, NonDetectionResponseProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
  }

  private aggregateNonDetections(
    results: FeatureCollection<Point, NonDetectionResponseProperties>[],
  ): Feature<Point, NonDetectionProperties>[] {
    const nonDetectionsByClusterId = new Map<string, Feature<Point, NonDetectionProperties>>()
    results.forEach((result) => {
      result.features.forEach((feature) => {
        if (feature.properties.clusterId == null) return

        const clusterId = String(feature.properties.clusterId)
        const existingCluster = nonDetectionsByClusterId.get(clusterId)

        if (existingCluster) {
          existingCluster.properties.count += 1
        } else {
          nonDetectionsByClusterId.set(clusterId, {
            type: 'Feature',
            id: clusterId,
            geometry: feature.geometry,
            properties: { clusterId, count: 1 },
          })
        }
      })
    })

    return Array.from(nonDetectionsByClusterId.values())
  }

  async loadNonDetections(
    tile: XyzTileModel,
  ): Promise<FeatureCollection<Point, NonDetectionResponseProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({ accept: 'json' }),
    }
    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, NonDetectionResponseProperties>>(
          this.getTileUrl(tile),
          httpOptions,
        )
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  getNonDetections(): FeatureCollection<Point, NonDetectionProperties> {
    return this.nonDetections
  }

  cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  ngOnDestroy(): void {
    this.cancelCurrentRequests()
    this.cancelPreviousRequest$.complete()
  }

  private getTileUrl(tile: XyzTileModel): string {
    return new URL(
      `${this.config.backend_url}vzo/nondetections/${tile.z}/${tile.x}/${tile.y}`,
    ).toString()
  }
}
